import React from "react";

import { Modals } from "../../../../commons/types/modal";
import ModalContainer from "../../components/ModalContainer/ModalContainer";
import usePrevious from "../../libs/hooks/use-previous";
import { useCommonAppDispatcher, useCommonAppSelector } from "../../store/hooks";
import { useKeyboardContext } from "../utils/keyboard-context";

interface Props {
  modals: Modals;
  /**
   * The animation duration in seconds
   */
  animationDuration?: React.ComponentProps<typeof ModalContainer>["animationDuration"];
}

const ModalPartial = ({ animationDuration, modals }: Props) => {
  const isOpen = useCommonAppSelector(state => state.modal.isOpen);
  const contentType = useCommonAppSelector(state => state.modal.modalConfig?.contentType);
  const contentProps = useCommonAppSelector(state => state.modal.modalConfig?.contentProps);
  const [dispatch, actions] = useCommonAppDispatcher();

  const close = () => dispatch(actions.modal.close());
  const { keyboardOpen } = useKeyboardContext();

  const modal = !!contentType && modals[contentType];

  if (!modal && !!contentType) {
    throw new Error(`Unknown modal contentType "${contentType}"`);
  }

  const previousIsOpen = usePrevious(isOpen);
  const previousContentProps = usePrevious(contentProps);
  React.useEffect(() => {
    if (!isOpen && previousIsOpen && previousContentProps?.onClose) {
      previousContentProps?.onClose();
    }
  }, [isOpen, contentType, previousContentProps, previousIsOpen]);

  // We need to return always a Modal continer because the closing animation is handled inside the container. Therefore it should close instead of return no component or a fragment.
  return (
    <ModalContainer
      animationDuration={animationDuration}
      isOpen={isOpen && contentType !== undefined}
      onClose={close}
      keyboardOpen={keyboardOpen}
    >
      {modal &&
        React.createElement(modal, {
          ...(contentProps || {}),
          close
        })}
    </ModalContainer>
  );
};

export default ModalPartial;
